/**
 * Plain names for the per-site switches (internal/sites/flags.go). The server's descriptions are
 * written for an AI reader; these are what staff see, with the server's text kept as a tooltip.
 * A switch the server adds later still shows (by its raw name and the server's description).
 */
export const switchLabel: Record<string, string> = {
  logins: "Password check",
  access: "Membership check",
  password_writes: "Password changes saved to",
  genpass: "New-member passwords",
  magic_links: "CRM sign-in links",
  autologin_code: "Old Keap autologin links",
  tag_writes: "Site tag changes go to",
  classifieds: "Club classified ads kept in",
  roster: "Roster file from",
  roster_passwords: "Passwords in the roster file",
  forms: "Site forms go to",
  buy_links: "Buy links go to",
  thank_you: "Old course thank-you pages",
  billing_portal: "Update-your-card links go to",
  reports: "Admin sales numbers from",
  customer_lookup: "Phone agent's caller lookup",
  purchase_automation: "Admin purchase step",
  bots_proxy: "Bots' data requests go to",
  affiliates: "CREU partner pages",
};

/** One line on what each switch decides. */
export const switchHelp: Record<string, string> = {
  logins: "Where the site checks a member's password.",
  access: "Where the site finds out what a member has paid for.",
  password_writes: "Where new and reset passwords are saved.",
  genpass: "Who makes the first password for a new member.",
  magic_links: "Sign-in and password-reset links emailed by the CRM.",
  autologin_code: "The shared autologin link in old Keap emails.",
  tag_writes: "Where the site saves the tags it sets (Club directory opt-in, Manager Training passed).",
  classifieds: "Where the Club's classified ads are stored.",
  roster: "Where the member roster file comes from.",
  roster_passwords: "Whether the roster file lists passwords (must be off once the roster comes from the CRM).",
  forms: "Where the site's contact and sign-up forms are sent.",
  buy_links: "Where Buy buttons and old Keap order-form links take people.",
  thank_you: "What the old Keap course thank-you pages do for CRM orders (needs buy links on the CRM).",
  billing_portal: "Where billing, receipt and update-your-card links take members.",
  reports: "Where admin.creuniversity.com's sales dashboard, sales email and Club member count get their numbers.",
  customer_lookup: "Where the phone agent looks up who is calling.",
  purchase_automation: "What admin's purchase step (called by Keap campaigns) reads: last order totals and Facebook audience moves.",
  bots_proxy: "Where the bots' data requests through admin.creuniversity.com go.",
  affiliates: "CREU's partner sign-up form and partner sign-in.",
};

const valueWords: Record<string, string> = {
  keap: "Keap",
  crm: "CRM",
  both: "CRM and Keap",
  shadow: "Keap, copy to CRM",
  on: "On",
  off: "Off",
  fortapay: "Keap (FortaPay)",
  club: "Club's own database",
};

const valueOverride: Record<string, Record<string, string>> = {
  password_writes: { keap: "Keap only", crm: "CRM only" },
  tag_writes: { crm: "CRM only" },
  genpass: { both: "Keap, copied to CRM", crm: "CRM (set-password link)" },
  magic_links: { off: "Off (site's own reset links)", on: "On" },
  autologin_code: { on: "On (old links work)", off: "Off (sign-in page)" },
  roster_passwords: { on: "On (listed)", off: "Off" },
  thank_you: { keap: "Keap page (as before)", crm: "CRM thank-you" },
};

/** What a switch value means, in plain words ("Keap", "CRM", "Off"...). */
export function valueLabel(sw: string, v: string): string {
  return valueOverride[sw]?.[v] ?? valueWords[v] ?? v;
}

/** The sentence for putting a switch back to its first value (how the site worked before the CRM). */
export function backToOriginal(sw: string, original: string): string {
  return original === "keap" || original === "fortapay" ? "This sends the site back to Keap." : `This puts the switch back to how the site worked before the CRM (${valueLabel(sw, original)}).`;
}

// The multi-switch dialog (Settings > Websites > Apply a set of switches).
// Several site switches at once (crm-sites' sites_flags_set takes them together and checks the combination).

export interface SiteRow {
  site: string;
  name: string;
  base_url: string;
  flags: Record<string, string>;
}

export interface SwitchDef {
  name: string;
  values: string[];
  default: string;
  sites: string[];
  description: string;
}

export const SWITCH_NAMES: Record<string, string> = switchLabel;

export const switchName = (n: string) => SWITCH_NAMES[n] ?? n.replace(/_/g, " ");

/** Friday run sheet steps 17 and 18: move a sign-in site's passwords and membership check to the CRM in one go. */
export const PRESETS: { site: string; label: string; step: string; flags: Record<string, string> }[] = [
  {
    site: "mhcmt",
    label: "Move Manager Training sign-ins to the CRM",
    step: "Run sheet step 17",
    flags: { logins: "crm", access: "crm", password_writes: "both", genpass: "both", magic_links: "on", tag_writes: "both" },
  },
  {
    site: "courses",
    label: "Move course sign-ins to the CRM",
    step: "Run sheet step 18",
    flags: { logins: "crm", access: "crm", password_writes: "both", genpass: "both", magic_links: "on" },
  },
];

/** The switches whose value would change: [name, from, to]. */
export function switchChanges(site: SiteRow, defs: SwitchDef[], wanted: Record<string, string>): [string, string, string][] {
  const out: [string, string, string][] = [];
  for (const d of defs) {
    const to = wanted[d.name];
    if (!to || !d.sites.includes(site.site)) continue;
    const from = site.flags[d.name] ?? d.default;
    if (from !== to) out.push([d.name, from, to]);
  }
  return out;
}

/** What sites_flags_set receives: a switch going back to its first value is sent as "default" (as the Sites table does). */
export function flagsBody(defs: SwitchDef[], changes: [string, string, string][]): Record<string, string> {
  const body: Record<string, string> = {};
  for (const [name, , to] of changes) body[name] = defs.find((d) => d.name === name)?.default === to ? "default" : to;
  return body;
}

/**
 * The server's combination rules (internal/sites/flags.go checkFlagCombination), checked here first so the preview can
 * say why it would be refused. The server checks again; this never lets anything through that it would refuse.
 */
export function combinationProblem(site: SiteRow, defs: SwitchDef[], wanted: Record<string, string>): string {
  const get = (n: string) => wanted[n] ?? site.flags[n] ?? defs.find((d) => d.name === n)?.default ?? "";
  if (get("logins") === "crm" && get("password_writes") === "keap")
    return "With the password check on the CRM, password changes must be saved to both or the CRM; otherwise a new password would only reach Keap and wouldn't work.";
  if (get("logins") === "keap" && get("password_writes") === "crm") return "While Keap checks passwords, password changes can't go to the CRM only.";
  if (get("roster") === "crm" && get("roster_passwords") === "on") return "With the roster from the CRM, passwords in the roster file must be off.";
  if (get("genpass") === "crm" && get("magic_links") === "off") return "New-member passwords from the CRM need CRM sign-in links on.";
  if (get("magic_links") === "on" && get("logins") === "keap") return "CRM sign-in links need the password check on the CRM.";
  if (get("thank_you") === "crm" && get("buy_links") !== "crm") return "Thank-you pages on the CRM need buy links on the CRM.";
  if (get("genpass") && get("genpass") !== "keap" && get("logins") === "keap") return "New-member passwords other than Keap need the password check on the CRM.";
  return "";
}
